import React, { useState, useEffect } from 'react';
import { TikTokRecipe } from '../../types/tiktok';
import { TikTokRecipeForm } from './TikTokRecipeForm';
import { TikTokIngredientMatcher } from './TikTokIngredientMatcher';
import { TikTokRecipeEditor } from './TikTokRecipeEditor';
import { MatchedIngredient } from '../../types/ingredients';

type BackendStatus = 'checking' | 'online' | 'offline';
type AppStep = 'form' | 'matching' | 'editor';

export const TikTokRecipeReader: React.FC = () => {
  const [recipe, setRecipe] = useState<TikTokRecipe | null>(null);
  const [finalRecipe, setFinalRecipe] = useState<TikTokRecipe | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backendStatus, setBackendStatus] = useState<BackendStatus>('checking');
  const [currentStep, setCurrentStep] = useState<AppStep>('form');

  const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001';

  useEffect(() => {
    checkBackend();
  }, []);

  const checkBackend = async () => {
    setBackendStatus('checking');
    try {
      const response = await fetch(`${backendUrl}/health`);
      const isOnline = response.ok;
      setBackendStatus(isOnline ? 'online' : 'offline');
    } catch {
      setBackendStatus('offline');
    }
  };

  const handleSubmit = async (url: string, targetLanguage?: string) => {
    setLoading(true);
    setError(null);

    try {
      const requestBody: { url: string; targetLanguage?: string } = { url };
      if (targetLanguage) {
        requestBody.targetLanguage = targetLanguage;
      }

      const response = await fetch(`${backendUrl}/api/extract-recipe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'extraction de la recette');
      }

      const result = await response.json();
      setRecipe(result.data.recipe);
      
      // Si une langue cible était spécifiée et que les ingrédients sont déjà matchés,
      // on peut passer directement à l'éditeur
      const hasMatches = result.data.recipe.ingredients.some(
        (ing: any) => ing.standardIngredientId
      );
      
      if (targetLanguage && hasMatches) {
        console.log('✅ Ingrédients déjà matchés par le backend, passage direct à l\'éditeur');
        setFinalRecipe(result.data.recipe);
        setCurrentStep('editor');
      } else {
        setCurrentStep('matching');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmMatches = (matches: MatchedIngredient[]) => {
    if (!recipe) return;

    const updatedRecipe: TikTokRecipe = {
      ...recipe,
      ingredients: matches.map(match => ({
        id: match.extracted.id,
        // Always prefer the selected standard ingredient name after manual matching.
        name: match.matched?.name || match.extracted.name,
        amount: match.extracted.amount,
        unit: match.extracted.unit,
        standardIngredientId: match.matched?.id,
        category: match.matched?.category,
        matchedStandardName: match.matched?.name,
      })),
    };

    setFinalRecipe(updatedRecipe);
    setCurrentStep('editor');
  };

  const handleSkipMatching = () => {
    setFinalRecipe(recipe);
    setCurrentStep('editor');
  };

  const handleSaveSuccess = () => {
    alert('✅ Recette sauvegardée avec succès !');
    handleReset();
  };

  const handleCancelEditor = () => {
    setCurrentStep('matching');
  };

  const handleReset = () => {
    setRecipe(null);
    setFinalRecipe(null);
    setError(null);
    setCurrentStep('form');
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">🎬 TikTok Recipe Reader</h2>
            <p className="text-gray-600 mt-1">Extrayez automatiquement des recettes depuis les vidéos TikTok</p>
          </div>
          {backendStatus === 'online' && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              ✨ Extraction automatique activée
            </span>
          )}
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">
              <strong>❌ Erreur:</strong> {error}
            </p>
          </div>
        )}

        {currentStep === 'form' && (
          <TikTokRecipeForm 
            onSubmit={handleSubmit} 
            loading={loading}
            backendStatus={backendStatus}
            onCheckBackend={checkBackend}
          />
        )}

        {currentStep === 'matching' && recipe && (
          <TikTokIngredientMatcher
            recipe={recipe}
            onConfirm={handleConfirmMatches}
            onSkip={handleSkipMatching}
          />
        )}

        {currentStep === 'editor' && finalRecipe && (
          <TikTokRecipeEditor 
            recipe={finalRecipe} 
            onSuccess={handleSaveSuccess}
            onCancel={handleCancelEditor}
          />
        )}
      </div>
    </div>
  );
};

